/**
 * Standardized Error Handling Service for Rankbet Application
 * Provides consistent error handling across all services
 */

export enum ErrorType {
  NETWORK = "NETWORK",
  AUTH = "AUTH",
  VALIDATION = "VALIDATION",
  NOT_FOUND = "NOT_FOUND",
  PERMISSION = "PERMISSION",
  SERVER = "SERVER",
  CLIENT = "CLIENT",
  UNKNOWN = "UNKNOWN",
}

export enum ErrorSeverity {
  LOW = "LOW", // Minor issues, app continues normally
  MEDIUM = "MEDIUM", // Important issues, some functionality affected
  HIGH = "HIGH", // Critical issues, major functionality broken
  CRITICAL = "CRITICAL", // App-breaking issues
}

export interface AppError {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  details?: string;
  code?: string | number;
  timestamp: Date;
  context?: Record<string, unknown>;
  originalError?: unknown;
}

export interface ApiResponse<T> {
  data: T | null;
  error: AppError | null;
  success: boolean;
}

export class ErrorHandlingService {
  private static instance: ErrorHandlingService;

  private constructor() {}

  public static getInstance(): ErrorHandlingService {
    if (!ErrorHandlingService.instance) {
      ErrorHandlingService.instance = new ErrorHandlingService();
    }
    return ErrorHandlingService.instance;
  }

  /**
   * Create a standardized error object
   */
  createError(
    type: ErrorType,
    severity: ErrorSeverity,
    message: string,
    details?: string,
    context?: Record<string, unknown>,
    originalError?: unknown
  ): AppError {
    return {
      type,
      severity,
      message,
      details,
      timestamp: new Date(),
      context,
      originalError,
    };
  }

  /**
   * Create a success response
   */
  createSuccess<T>(data: T): ApiResponse<T> {
    return {
      data,
      error: null,
      success: true,
    };
  }

  /**
   * Create an error response
   */
  createErrorResponse<T>(error: AppError): ApiResponse<T> {
    return {
      data: null,
      error,
      success: false,
    };
  }

  /**
   * Handle HTTP Response errors
   */
  async handleHttpResponse<T>(
    response: Response,
    context?: Record<string, unknown>
  ): Promise<ApiResponse<T>> {
    if (!response.ok) {
      let errorText = "";
      try {
        errorText = await response.text();
      } catch {
        errorText = "Could not read error response";
      }

      const error = this.createError(
        this.mapHttpStatusToErrorType(response.status),
        this.mapHttpStatusToSeverity(response.status),
        `HTTP ${response.status}: ${response.statusText}`,
        errorText,
        {
          url: response.url,
          status: response.status,
          ...context,
        }
      );

      this.logError(error);
      return this.createErrorResponse(error);
    }

    try {
      const data = await response.json();
      return this.createSuccess(data);
    } catch (parseError) {
      const error = this.createError(
        ErrorType.CLIENT,
        ErrorSeverity.MEDIUM,
        "Failed to parse response JSON",
        "Response body is not valid JSON",
        { url: response.url, ...context },
        parseError
      );

      this.logError(error);
      return this.createErrorResponse(error);
    }
  }

  /**
   * Handle generic JavaScript errors
   */
  handleJSError(
    originalError: unknown,
    message: string,
    context?: Record<string, unknown>
  ): AppError {
    const error = this.createError(
      ErrorType.UNKNOWN,
      ErrorSeverity.MEDIUM,
      message,
      this.extractErrorMessage(originalError),
      context,
      originalError
    );

    this.logError(error);
    return error;
  }

  /**
   * Handle Supabase errors
   */
  handleSupabaseError(
    supabaseError: any,
    operation: string,
    context?: Record<string, unknown>
  ): AppError {
    const error = this.createError(
      this.mapSupabaseCodeToErrorType(supabaseError?.code),
      ErrorSeverity.MEDIUM,
      `Supabase ${operation} failed: ${supabaseError?.message || "Unknown error"}`,
      supabaseError?.details || supabaseError?.hint,
      {
        operation,
        supabaseCode: supabaseError?.code,
        ...context,
      },
      supabaseError
    );

    this.logError(error);
    return error;
  }

  /**
   * Log error with appropriate level
   */
  private logError(error: AppError): void {
    const logMessage = `[${error.type}/${error.severity}] ${error.message}`;

    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
        console.error("🔥 CRITICAL:", logMessage, {
          details: error.details,
          context: error.context,
          timestamp: error.timestamp,
        });
        break;
      case ErrorSeverity.HIGH:
        console.error("❌ HIGH:", logMessage, {
          details: error.details,
          context: error.context,
        });
        break;
      case ErrorSeverity.MEDIUM:
        console.warn("⚠️ MEDIUM:", logMessage, {
          details: error.details,
          context: error.context,
        });
        break;
      case ErrorSeverity.LOW:
        console.info("ℹ️ LOW:", logMessage);
        break;
    }
  }

  /**
   * Map HTTP status codes to error types
   */
  private mapHttpStatusToErrorType(status: number): ErrorType {
    if (status >= 400 && status < 500) {
      switch (status) {
        case 401:
          return ErrorType.AUTH;
        case 403:
          return ErrorType.PERMISSION;
        case 404:
          return ErrorType.NOT_FOUND;
        case 422:
          return ErrorType.VALIDATION;
        default:
          return ErrorType.CLIENT;
      }
    }
    if (status >= 500) {
      return ErrorType.SERVER;
    }
    return ErrorType.NETWORK;
  }

  /**
   * Map HTTP status codes to severity
   */
  private mapHttpStatusToSeverity(status: number): ErrorSeverity {
    if (status === 404) return ErrorSeverity.LOW;
    if (status >= 400 && status < 500) return ErrorSeverity.MEDIUM;
    if (status >= 500) return ErrorSeverity.HIGH;
    return ErrorSeverity.MEDIUM;
  }

  /**
   * Map Supabase error codes to error types
   */
  private mapSupabaseCodeToErrorType(code?: string): ErrorType {
    if (!code) return ErrorType.UNKNOWN;

    switch (code) {
      case "PGRST116":
        return ErrorType.NOT_FOUND;
      case "PGRST204":
        return ErrorType.PERMISSION;
      case "PGRST301":
        return ErrorType.VALIDATION;
      default:
        return ErrorType.SERVER;
    }
  }

  /**
   * Extract meaningful error message from unknown error
   */
  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === "string") {
      return error;
    }
    if (error && typeof error === "object" && "message" in error) {
      return String((error as any).message);
    }
    return "Unknown error occurred";
  }
}

export const errorHandler = ErrorHandlingService.getInstance();
